import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
    getFlatTransferDetail,
    reverseFlatTransfer,
    addFlatTransferDocument
} from '../../../../redux/actions/SocietyPortal/FlatTransferAction/FlatTransferAction';
import { canAny } from '../../../../utils/helpers/can';
import { PERMISSIONS } from '../../../../consts/permissions';
import { usePageRefresh } from '../../../../context/PageRefreshContext';

const DOCUMENT_TYPES = [
    'SALE_DEED', 'GIFT_DEED', 'RELEASE_DEED', 'NOC', 'AADHAAR', 'PAN',
    'PASSPORT', 'BUYER_PHOTO', 'OLD_SHARE_CERTIFICATE', 'NEW_SHARE_CERTIFICATE',
    'TRANSFER_APPLICATION', 'BOARD_RESOLUTION', 'OTHER'
];

const statusColor = (s) => {
    switch (s) {
        case 'POSTED': return 'tw-bg-emerald-100 tw-text-emerald-700';
        case 'REVERSED': return 'tw-bg-amber-100 tw-text-amber-700';
        default: return 'tw-bg-gray-100 tw-text-gray-700';
    }
};

const FlatTransferDetail = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const userPermissions = useSelector(s => s.UserSocietyReducer?.permissions || []);
    const { detail, reverse, documents } = useSelector(state => state.FlatTransferReducer);
    const data = detail?.data;

    const [showReverseModal, setShowReverseModal] = useState(false);
    const [reverseReason, setReverseReason] = useState('');
    const [showDocModal, setShowDocModal] = useState(false);
    const [doc, setDoc] = useState({
        document_type: 'SALE_DEED',
        document_label: '',
        file_url: '',
        file_name: '',
        file_size_bytes: '',
        mime_type: ''
    });

    useEffect(() => {
        if (id) dispatch(getFlatTransferDetail({ id }));
    }, [dispatch, id]);

    const canReverse = canAny(userPermissions, [PERMISSIONS.FLAT_TRANSFER_REVERSE]);
    const canAddDocs = canAny(userPermissions, [PERMISSIONS.FLAT_TRANSFER_CREATE]);

    const refresh = () => dispatch(getFlatTransferDetail({ id }));
    usePageRefresh(refresh);

    const handleReverse = () => {
        dispatch(reverseFlatTransfer({
            id,
            reason: reverseReason,
            onSuccess: () => {
                setShowReverseModal(false);
                setReverseReason('');
                refresh();
            }
        }));
    };

    const handleAddDoc = () => {
        dispatch(addFlatTransferDocument({
            id,
            ...doc,
            file_size_bytes: doc.file_size_bytes ? Number(doc.file_size_bytes) : undefined,
            onSuccess: () => {
                setShowDocModal(false);
                setDoc({
                    document_type: 'SALE_DEED',
                    document_label: '',
                    file_url: '',
                    file_name: '',
                    file_size_bytes: '',
                    mime_type: ''
                });
                refresh();
            }
        }));
    };

    if (detail?.loading) {
        return (
            <div className="tw-py-10 tw-flex tw-justify-center">
                <div className="tw-animate-spin tw-rounded-full tw-h-7 tw-w-7 tw-border-b-2 tw-border-blue-600" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-text-center tw-shadow-sm tw-border tw-border-gray-100">
                <p className="tw-text-sm tw-text-gray-500">Transfer not found.</p>
            </div>
        );
    }

    const isPosted = data.status === 'POSTED';

    return (
        <div className="tw-space-y-4">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button onClick={() => navigate(-1)} className="tw-p-1.5 tw-rounded-md tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                        <Icon icon="mdi:arrow-left" className="tw-text-gray-600" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Transfer #{data.id}</h1>
                        <p className="tw-text-xs tw-text-gray-500">
                            {data.flat?.wing?.title} • Flat {data.flat?.flat_number} • {data.transfer_date}
                        </p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <span className={`tw-px-2.5 tw-py-1 tw-rounded tw-text-xs tw-font-semibold ${statusColor(data.status)}`}>
                        {data.status}
                    </span>
                    {isPosted && canReverse && (
                        <button
                            onClick={() => setShowReverseModal(true)}
                            className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-border tw-border-amber-500 tw-text-amber-700 hover:tw-bg-amber-50"
                        >
                            <Icon icon="mdi:undo" className="tw-inline tw-mr-1" />
                            Reverse
                        </button>
                    )}
                    {canAddDocs && (
                        <button
                            onClick={() => setShowDocModal(true)}
                            className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700"
                        >
                            <Icon icon="mdi:paperclip" className="tw-inline tw-mr-1" />
                            Attach Document
                        </button>
                    )}
                </div>
            </div>

            {/* Parties */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-2">From</h3>
                    <p className="tw-text-sm tw-text-gray-800">{data.oldEntity?.name || data.oldEntity?.first_name}</p>
                    <p className="tw-text-xs tw-text-gray-500">{data.oldEntity?.phone}</p>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-2">To</h3>
                    <p className="tw-text-sm tw-text-gray-800">{data.newEntity?.name || data.newEntity?.first_name}</p>
                    <p className="tw-text-xs tw-text-gray-500">{data.newEntity?.phone}</p>
                </div>
            </div>

            {/* Meta */}
            <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3">Transfer Meta</h3>
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-text-sm">
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Reason</div>
                        <div className="tw-text-gray-800">{data.transfer_reason}</div>
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Settlement</div>
                        <div className="tw-text-gray-800">{data.balance_settlement_mode}</div>
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Fee (for record)</div>
                        <div className="tw-text-gray-800">
                            {Number(data.transfer_fee_amount || 0) > 0
                                ? `₹${Number(data.transfer_fee_amount).toLocaleString('en-IN')}${data.fee_journal_id ? ` (${data.transfer_fee_charged_to})` : ''}`
                                : 'None'}
                        </div>
                        {Number(data.transfer_fee_amount || 0) > 0 && !data.fee_journal_id && (
                            <p className="tw-text-[11px] tw-text-gray-400 tw-mt-0.5">Not posted — collect via separate receipt.</p>
                        )}
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Sale consideration</div>
                        <div className="tw-text-gray-800">
                            {data.sale_consideration ? `₹${Number(data.sale_consideration).toLocaleString('en-IN')}` : '—'}
                        </div>
                    </div>
                </div>
                {data.remarks && (
                    <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100">
                        <div className="tw-text-xs tw-text-gray-500 tw-mb-1">Remarks</div>
                        <p className="tw-text-sm tw-text-gray-700">{data.remarks}</p>
                    </div>
                )}
            </div>

            {/* Documents */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Documents</h3>
                </div>
                {(data.documents || []).length === 0 ? (
                    <p className="tw-text-sm tw-text-gray-500 tw-text-center tw-py-6">No documents attached.</p>
                ) : (
                    <table className="tw-w-full tw-text-sm">
                        <thead className="tw-bg-gray-50">
                            <tr className="tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Type</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Label</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">File</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Open</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.documents.map(d => (
                                <tr key={d.id} className="tw-border-b tw-border-gray-50">
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-700">{d.document_type}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-700">{d.document_label}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs">{d.file_name}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-center">
                                        <a href={d.file_url} target="_blank" rel="noreferrer" className="tw-text-blue-600 hover:tw-underline">
                                            <Icon icon="mdi:open-in-new" />
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Reverse modal */}
            {showReverseModal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full tw-mx-4 tw-p-5">
                        <h3 className="tw-text-base tw-font-semibold tw-text-gray-800 tw-mb-2">Reverse this transfer?</h3>
                        <p className="tw-text-xs tw-text-gray-500 tw-mb-3">
                            This will restore the old owner, re-activate old GLs and cancel the issued share certificate.
                        </p>
                        <label className="tw-text-xs tw-text-gray-600">Reason</label>
                        <textarea
                            rows={3}
                            value={reverseReason}
                            onChange={(e) => setReverseReason(e.target.value)}
                            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                        />
                        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                            <button
                                onClick={() => setShowReverseModal(false)}
                                className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-border tw-border-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReverse}
                                disabled={!reverseReason.trim() || reverse?.loading}
                                className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-bg-amber-600 tw-text-white hover:tw-bg-amber-700 disabled:tw-opacity-60"
                            >
                                {reverse?.loading ? 'Reversing…' : 'Reverse Transfer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add document modal */}
            {showDocModal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-lg tw-w-full tw-mx-4 tw-p-5 tw-space-y-3">
                        <h3 className="tw-text-base tw-font-semibold tw-text-gray-800">Attach Document</h3>
                        <p className="tw-text-xs tw-text-gray-500">
                            Upload the file via your storage flow first, then paste the URL here.
                        </p>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-text-sm">
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Type</label>
                                <select
                                    value={doc.document_type}
                                    onChange={(e) => setDoc({ ...doc, document_type: e.target.value })}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white"
                                >
                                    {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Label</label>
                                <input
                                    value={doc.document_label}
                                    onChange={(e) => setDoc({ ...doc, document_label: e.target.value })}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                            <div className="md:tw-col-span-2">
                                <label className="tw-text-xs tw-text-gray-600">File URL</label>
                                <input
                                    value={doc.file_url}
                                    onChange={(e) => setDoc({ ...doc, file_url: e.target.value })}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">File name</label>
                                <input
                                    value={doc.file_name}
                                    onChange={(e) => setDoc({ ...doc, file_name: e.target.value })}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Mime type</label>
                                <input
                                    value={doc.mime_type}
                                    onChange={(e) => setDoc({ ...doc, mime_type: e.target.value })}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-text-gray-600">Size (bytes)</label>
                                <input
                                    type="number"
                                    value={doc.file_size_bytes}
                                    onChange={(e) => setDoc({ ...doc, file_size_bytes: e.target.value })}
                                    className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                                />
                            </div>
                        </div>
                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <button
                                onClick={() => setShowDocModal(false)}
                                className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-border tw-border-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddDoc}
                                disabled={!doc.file_url || documents?.uploading}
                                className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-60"
                            >
                                {documents?.uploading ? 'Attaching…' : 'Attach'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlatTransferDetail;
